// "Add to Calendar" link/file builders for season events (recitals).
//
// Season events carry a DATE only (no wall-clock time) — see src/canon/season.json —
// so every event is emitted as an ALL-DAY event. All-day end is exclusive (the day
// after), per both the Google render API and RFC 5545 DTEND;VALUE=DATE.
//
// Pure + client-only: no network, no backend. googleCalendarUrl / buildIcs are pure
// (unit-tested); downloadIcs touches the DOM and is the only side-effecting export.

// 'YYYY-MM-DD' -> 'YYYYMMDD'.
const compact = (ymd) => ymd.replace(/-/g, '');

// The calendar day after 'YYYY-MM-DD' as 'YYYYMMDD' (handles month/year rollover).
// UTC math so a whole-day step never crosses a DST edge.
const nextDayCompact = (ymd) => {
	const [y, m, d] = ymd.split('-').map(Number);
	const dt = new Date(Date.UTC(y, m - 1, d));
	dt.setUTCDate(dt.getUTCDate() + 1);
	const p = (n) => String(n).padStart(2, '0');
	return `${dt.getUTCFullYear()}${p(dt.getUTCMonth() + 1)}${p(dt.getUTCDate())}`;
};

// A Google Calendar "add event" URL for an all-day event on `date` ('YYYY-MM-DD').
export const googleCalendarUrl = ({ title, date, details = '', location = '' }) => {
	const params = new URLSearchParams({
		action: 'TEMPLATE',
		text: title,
		dates: `${compact(date)}/${nextDayCompact(date)}`,
	});
	if (details) params.set('details', details);
	if (location) params.set('location', location);
	return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// Escape a text value for an iCalendar property (RFC 5545 §3.3.11).
const icsEscape = (s) =>
	String(s)
		.replace(/\\/g, '\\\\')
		.replace(/;/g, '\\;')
		.replace(/,/g, '\\,')
		.replace(/\r?\n/g, '\\n');

// A deterministic UID slug so re-adding the same event de-dupes in the calendar.
const slug = (s) =>
	String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// A single-VEVENT .ics document (Apple Calendar / Outlook) for an all-day event.
// `dtstamp` is injectable ('YYYYMMDDTHHMMSSZ') so the output is testable; callers
// normally omit it and get the current UTC instant.
export const buildIcs = ({ title, date, details = '', location = '', dtstamp } = {}) => {
	const stamp =
		dtstamp || new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
	const uid = `${date}-${slug(title)}@coloryourvoice.com`;
	const lines = [
		'BEGIN:VCALENDAR',
		'VERSION:2.0',
		'PRODID:-//Color Your Voice//Season Calendar//EN',
		'CALSCALE:GREGORIAN',
		'BEGIN:VEVENT',
		`UID:${uid}`,
		`DTSTAMP:${stamp}`,
		`DTSTART;VALUE=DATE:${compact(date)}`,
		`DTEND;VALUE=DATE:${nextDayCompact(date)}`,
		`SUMMARY:${icsEscape(title)}`,
		details ? `DESCRIPTION:${icsEscape(details)}` : null,
		location ? `LOCATION:${icsEscape(location)}` : null,
		'TRANSP:TRANSPARENT',
		'END:VEVENT',
		'END:VCALENDAR',
	].filter(Boolean);
	// RFC 5545 mandates CRLF line breaks.
	return lines.join('\r\n');
};

// Trigger a client download of an .ics file. DOM side-effect; guarded for SSR/tests.
export const downloadIcs = (filename, icsString) => {
	if (typeof document === 'undefined') return;
	const blob = new Blob([icsString], { type: 'text/calendar;charset=utf-8' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename.endsWith('.ics') ? filename : `${filename}.ics`;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
};
